import { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, Send, KeyRound, X, Undo2, Trash2, Clock } from "lucide-react";
import { API, errMsg } from "@/lib/api";
import { payMsg } from "@/components/admin/SalesMessages";

const STATUS = {
  escrito: ["Ha escrito", "bg-[var(--butter)]"],
  pago_enviado: ["Pago enviado", "bg-[#F2C3AE]"],
  pagado: ["Pagado", "bg-[#C9D8C5]"],
  descartado: ["Descartado", "bg-[#e7e0d6] text-[#7a6c5e]"],
};
const PENDING = ["escrito", "pago_enviado"];

const waTo = (phone, text) => {
  let d = (phone || "").replace(/\D/g, "");
  if (d.length === 9) d = `34${d}`;
  return d ? `https://wa.me/${d}?text=${encodeURIComponent(text)}` : null;
};

const ago = (iso) => {
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 36e5);
  if (h < 1) return "hace un momento";
  if (h < 24) return `hace ${h} h`;
  const d = Math.floor(h / 24);
  return `hace ${d} ${d === 1 ? "día" : "días"}`;
};

const field = "rounded-xl border-2 border-[#d9cbb8] focus:border-[var(--ink)] outline-none px-3 py-2 bg-[var(--cream)]";
const act = "inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm font-semibold border-2 border-[var(--ink)] hover:bg-[var(--ink)] hover:text-[var(--cream)] transition-colors";

const LeadForm = ({ onAdd }) => {
  const [f, setF] = useState({ name: "", phone: "", email: "", note: "", product_id: "equipo-calma" });
  const submit = async (e) => { e.preventDefault(); if (await onAdd(f)) setF({ name: "", phone: "", email: "", note: "", product_id: "equipo-calma" }); };
  return (
    <form onSubmit={submit} className="grid sm:grid-cols-2 lg:grid-cols-3 gap-2">
      <input data-testid="lead-name-input" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} placeholder="Nombre" className={field} required />
      <input data-testid="lead-phone-input" value={f.phone} onChange={(e) => setF({ ...f, phone: e.target.value })} placeholder="Teléfono" className={field} />
      <input data-testid="lead-email-input" type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} placeholder="Correo electrónico" className={field} />
      <select data-testid="lead-product-select" value={f.product_id} onChange={(e) => setF({ ...f, product_id: e.target.value })} className={field}>
        <option value="equipo-calma">Equipo en Calma</option>
        <option value="rabietas-2-3">Rabietas · 2–3 años</option>
      </select>
      <input data-testid="lead-note-input" value={f.note} onChange={(e) => setF({ ...f, note: e.target.value })} placeholder="Nota (opcional)" className={field} />
      <button data-testid="add-lead-button" className="btn-accent !py-2"><Plus size={16} /> Añadir</button>
    </form>
  );
};

const LeadRow = ({ l, settings, codeMessage, onStatus, onPaid, onDelete }) => {
  const [label, color] = STATUS[l.status];
  const late = PENDING.includes(l.status) && Date.now() - new Date(l.created_at).getTime() > 48 * 36e5;
  const sendPay = () => {
    const cardLink = l.product_id === "rabietas-2-3" ? settings.card_link_rabietas : settings.card_link;
    if (!cardLink && !settings.bizum_number) {
      toast.error("Primero guarda tu enlace de tarjeta o tu Bizum en \"Mensajes de venta\", más abajo.");
      return;
    }
    const msg = payMsg(l.name, settings, l.product_id);
    const url = waTo(l.phone, msg);
    if (url) window.open(url, "_blank", "noopener");
    else if (l.email) window.location.href = `mailto:${encodeURIComponent(l.email)}?subject=${encodeURIComponent("Instrucciones de pago")}&body=${encodeURIComponent(msg)}`;
    else { navigator.clipboard?.writeText(msg); toast.success("Mensaje de pago copiado"); }
    if (l.status === "escrito") onStatus(l, "pago_enviado");
  };
  const sendCode = () => {
    const msg = codeMessage({ buyer: l.name, code: l.code, product_id: l.product_id });
    const url = waTo(l.phone, msg);
    if (url) window.open(url, "_blank", "noopener");
    else if (l.email) window.location.href = `mailto:${encodeURIComponent(l.email)}?subject=${encodeURIComponent("Tu código de acceso")}&body=${encodeURIComponent(msg)}`;
    else { navigator.clipboard?.writeText(msg); toast.success("Mensaje con el código copiado"); }
  };
  return (
    <div data-testid={`lead-row-${l.id}`} className={`rounded-xl bg-white p-4 border-2 ${late ? "border-[var(--terra)]" : "border-transparent"}`}>
      <div className="flex flex-wrap items-center gap-2">
        <p className="font-semibold">{l.name}</p>
        {l.phone && <span className="text-sm text-[#7a6c5e]">{l.phone}</span>}
        {l.email && <a href={`mailto:${l.email}`} className="text-sm text-[#7a6c5e] underline">{l.email}</a>}
        <span data-testid={`lead-status-${l.id}`} className={`text-xs font-semibold rounded-full px-2.5 py-1 ${color}`}>{label}</span>
        <span className={`text-xs inline-flex items-center gap-1 ml-auto ${late ? "text-[var(--terra)] font-semibold" : "text-[#7a6c5e]"}`}>
          <Clock size={12} /> {late ? `Lleva ${ago(l.created_at).replace("hace ", "")} esperando` : ago(l.created_at)}
        </span>
      </div>
      {l.note && <p className="text-sm text-[#5a4f46] mt-1">{l.note}</p>}
      {l.code && <p className="text-sm mt-1">Código: <b data-testid={`lead-code-${l.id}`}>{l.code}</b></p>}
      <div className="flex flex-wrap gap-2 mt-3">
        {PENDING.includes(l.status) && <>
          <button data-testid={`lead-send-pay-${l.id}`} onClick={sendPay} className={act}><Send size={14} /> {l.status === "escrito" ? "Enviar pago" : "Reenviar pago"}</button>
          <button data-testid={`lead-paid-${l.id}`} onClick={() => onPaid(l)} className={`${act} !bg-[var(--sage-deep)] !text-white !border-[var(--sage-deep)]`}><KeyRound size={14} /> Pagado · crear código</button>
          <button data-testid={`lead-discard-${l.id}`} onClick={() => onStatus(l, "descartado")} className={`${act} !border-[#d9cbb8]`}><X size={14} /> Descartar</button>
        </>}
        {l.status === "pagado" && <button data-testid={`lead-send-code-${l.id}`} onClick={sendCode} className={act}><Send size={14} /> Enviar código</button>}
        {l.status === "descartado" && <>
          <button data-testid={`lead-restore-${l.id}`} onClick={() => onStatus(l, "escrito")} className={act}><Undo2 size={14} /> Recuperar</button>
          <button data-testid={`lead-delete-${l.id}`} onClick={() => onDelete(l)} className={`${act} !border-[#d9cbb8] text-[#c0392b]`}><Trash2 size={14} /> Borrar</button>
        </>}
      </div>
    </div>
  );
};

export const PendingSales = ({ token, codeMessage, onCodeCreated }) => {
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [leads, setLeads] = useState([]);
  const [settings, setSettings] = useState({ card_link: "", bizum_number: "" });
  const [tab, setTab] = useState("pending");

  const load = useCallback(() => axios.get(`${API}/admin/leads`, auth).then((r) => setLeads(r.data)).catch(() => {}), [auth]);
  useEffect(() => {
    load();
    axios.get(`${API}/admin/settings`, auth).then((r) => setSettings(r.data)).catch(() => {});
  }, [load, auth]);

  const call = async (fn, ok) => { try { const r = await fn(); if (ok) toast.success(ok); load(); return r || true; } catch (e) { toast.error(errMsg(e)); return false; } };
  const add = (f) => call(() => axios.post(`${API}/admin/leads`, f, auth), "Añadida a ventas pendientes");
  const status = (l, s) => call(() => axios.patch(`${API}/admin/leads/${l.id}`, { status: s }, auth));
  const paid = async (l) => {
    if (!window.confirm(`¿Confirmas que ${l.name} ha pagado? Se creará su código.`)) return;
    const r = await call(() => axios.post(`${API}/admin/leads/${l.id}/paid`, {}, auth), "Código creado");
    if (r?.data) onCodeCreated(r.data.code);
  };
  const remove = (l) => window.confirm(`¿Borrar a ${l.name}?`) && call(() => axios.delete(`${API}/admin/leads/${l.id}`, auth));

  const pending = leads.filter((l) => PENDING.includes(l.status));
  const shown = tab === "pending" ? pending : leads.filter((l) => !PENDING.includes(l.status));

  return (
    <section data-testid="pending-sales" className="mt-8 rounded-2xl bg-[var(--cream-2)] p-4 sm:p-6">
      <div className="flex flex-wrap items-center gap-3">
        <p className="font-display text-2xl">Ventas pendientes</p>
        <span data-testid="pending-count" className="rounded-full bg-[var(--accent)] text-white text-sm font-semibold px-2.5 py-0.5">{pending.length}</span>
      </div>
      <p className="text-sm text-[#5a4f46] mt-1 mb-4">Apunta aquí a cada persona que te escriba para comprar y síguela hasta que tenga su código.</p>
      <LeadForm onAdd={add} />
      <div className="flex gap-2 mt-5">
        {[["pending", `Pendientes (${pending.length})`], ["history", "Historial"]].map(([k, t]) => (
          <button key={k} data-testid={`leads-tab-${k}`} onClick={() => setTab(k)} className={`rounded-full px-4 py-2 text-sm font-semibold ${tab === k ? "bg-[var(--ink)] text-[var(--cream)]" : "bg-white"}`}>{t}</button>
        ))}
      </div>
      <div className="mt-4 space-y-3">
        {shown.map((l) => <LeadRow key={l.id} l={l} settings={settings} codeMessage={codeMessage} onStatus={status} onPaid={paid} onDelete={remove} />)}
        {!shown.length && <p data-testid="leads-empty" className="text-sm text-[#7a6c5e]">{tab === "pending" ? "No hay nadie esperando. ¡Todo al día!" : "Aún no hay ventas cerradas."}</p>}
      </div>
    </section>
  );
};
